import { useState, useCallback } from 'react';
import { Message, AgentPublicInfo, SessionMode } from '../types';
import { connectToAgent, disconnectAgent, getAgentInfo, resumeConversation, processAgentAudio, processAgentText, sendTextChat } from '../api/client';
import { useAudioRecorder } from './useAudioRecorder';
import { WebSpeechSttAdapter } from '../adapters/stt/web-speech.adapter';

type SessionState = 'idle' | 'recording' | 'processing' | 'playing' | 'error';

const webSpeechStt = new WebSpeechSttAdapter();

export function useAgentSession() {
  const [apiKey, setApiKey] = useState('');
  const [sessionId, setSessionId] = useState<string | null>(null);
  const [agentInfo, setAgentInfo] = useState<AgentPublicInfo | null>(null);
  const [messages, setMessages] = useState<Message[]>([]);
  const [state, setState] = useState<SessionState>('idle');
  const [error, setError] = useState<string | null>(null);
  const [useBrowserStt, setUseBrowserStt] = useState(false);
  const { startRecording, stopRecording } = useAudioRecorder();

  const addMessage = useCallback((msg: Omit<Message, 'id' | 'timestamp'>) => {
    const message: Message = {
      ...msg,
      id: crypto.randomUUID(),
      timestamp: new Date(),
    };
    setMessages((prev) => [...prev, message]);
  }, []);

  const connect = useCallback(async (key: string, mode?: SessionMode, resumeSessionId?: string) => {
    try {
      setError(null);
      // Resume an existing conversation (a session id or a /api/std thread id)
      // instead of creating a new one. resumeConversation validates the id
      // against the agent key (invalid/foreign -> 401, we never enter the
      // chat) and returns the transcript so the resumed chat shows history.
      if (resumeSessionId) {
        const [agent, session] = await Promise.all([
          getAgentInfo(key),
          resumeConversation(key, resumeSessionId),
        ]);
        setApiKey(key);
        setSessionId(resumeSessionId);
        setAgentInfo(agent);
        setMessages(
          session.messages.map((m) => ({
            id: crypto.randomUUID(),
            role: m.role,
            text: m.text,
            timestamp: new Date(m.timestamp),
          })),
        );
        return { sessionId: resumeSessionId, agent };
      }
      const resp = await connectToAgent(key, mode);
      setApiKey(key);
      setSessionId(resp.sessionId);
      setAgentInfo(resp.agent);
      return resp;
    } catch (e: any) {
      const msg = e.response?.data?.message || e.message;
      setError(msg);
      throw e;
    }
  }, []);

  const disconnect = useCallback(() => {
    if (apiKey && sessionId) {
      disconnectAgent(apiKey, sessionId).catch(() => {});
    }
    setSessionId(null);
    setAgentInfo(null);
    setMessages([]);
    setState('idle');
    setError(null);
  }, [apiKey, sessionId]);

  const startVoiceInput = useCallback(async () => {
    setError(null);
    try {
      setState('recording');
      if (useBrowserStt) {
        webSpeechStt.startListening();
      } else {
        await startRecording();
      }
    } catch (e: any) {
      setError(e.message);
      setState('error');
    }
  }, [useBrowserStt, startRecording]);

  const stopVoiceInput = useCallback(async () => {
    if (!sessionId) return;
    setState('processing');
    try {
      let result;

      if (useBrowserStt) {
        const sttResult = await webSpeechStt.stopListening();
        addMessage({ role: 'user', text: sttResult.text });
        result = await processAgentText(apiKey, sessionId, sttResult.text);
      } else {
        const { blob } = await stopRecording();
        result = await processAgentAudio(apiKey, sessionId, blob);
        addMessage({ role: 'user', text: result.transcribedText });
      }

      addMessage({
        role: 'assistant',
        text: result.llmResponseText,
        metrics: result.metrics,
      });

      setState('playing');
      if (result.audio) {
        const url = URL.createObjectURL(result.audio);
        const audio = new Audio(url);
        await new Promise<void>((resolve) => {
          audio.onended = () => { URL.revokeObjectURL(url); resolve(); };
          audio.onerror = () => { URL.revokeObjectURL(url); resolve(); };
          audio.play();
        });
      }

      setState('idle');
    } catch (e: any) {
      setError(e.response?.data?.message || e.message);
      setState('error');
    }
  }, [apiKey, sessionId, useBrowserStt, stopRecording, addMessage]);

  const sendTextMessage = useCallback(async (text: string) => {
    if (!sessionId || !text.trim()) return;
    setState('processing');
    setError(null);
    try {
      addMessage({ role: 'user', text: text.trim() });
      const result = await processAgentText(apiKey, sessionId, text.trim());

      addMessage({
        role: 'assistant',
        text: result.llmResponseText,
        metrics: result.metrics,
      });

      setState('playing');
      if (result.audio) {
        const url = URL.createObjectURL(result.audio);
        const audio = new Audio(url);
        await new Promise<void>((resolve) => {
          audio.onended = () => { URL.revokeObjectURL(url); resolve(); };
          audio.onerror = () => { URL.revokeObjectURL(url); resolve(); };
          audio.play();
        });
      }

      setState('idle');
    } catch (e: any) {
      setError(e.response?.data?.message || e.message);
      setState('error');
    }
  }, [apiKey, sessionId, addMessage]);

  const sendTextOnly = useCallback(
    async (text: string) => {
      if (!sessionId || !text.trim()) return;
      setState('processing');
      setError(null);
      try {
        addMessage({ role: 'user', text: text.trim() });
        const result = await sendTextChat(apiKey, sessionId, text.trim());

        addMessage({
          role: 'assistant',
          text: result.response,
          metrics: {
            llm: {
              processingTimeMs: result.metrics.llmLatencyMs,
              inputTokens: result.metrics.inputTokens,
              outputTokens: result.metrics.outputTokens,
              cost: result.metrics.cost,
            },
            totalLatencyMs: result.metrics.llmLatencyMs,
            totalCost: result.metrics.cost,
          },
        });

        setState('idle');
      } catch (e: any) {
        setError(e.response?.data?.message || e.message);
        setState('error');
      }
    },
    [apiKey, sessionId, addMessage],
  );

  const clearMessages = useCallback(() => setMessages([]), []);

  return {
    apiKey,
    sessionId,
    agentInfo,
    messages,
    state,
    error,
    useBrowserStt,
    setUseBrowserStt,
    connect,
    disconnect,
    startVoiceInput,
    stopVoiceInput,
    sendTextMessage,
    sendTextOnly,
    clearMessages,
  };
}
